/**
 * Landing Page Add to Cart Module
 *
 * Handles add-to-cart functionality from landing page CTAs and the announcement bar modal.
 * Uses hash-based convention for triggering:
 *   - #add_to_cart                     - uses discount from URL/default, product from discount pattern
 *   - #add_to_cart:DISCOUNT            - uses specified discount, product from discount pattern
 *   - #add_to_cart::PRODUCT            - uses discount from URL/default, explicit product handle
 *   - #add_to_cart:DISCOUNT:PRODUCT    - uses explicit discount and product handle
 *
 * Resolves variant/selling plan from window.__CGC.acquisitionOffers
 */

const SHOPIFY_STOREFRONT_TOKEN = 'd0c759630bac3d3ae47cb98c66616ca1';

/**
 * Get product handle based on discount code pattern matching
 * Replicates logic from SubscriptionCart.#getProductHandleByDiscountCode()
 *
 * @param {string} discountCode - The discount code to match
 * @param {string} [defaultHandle] - Fallback handle if no pattern matches (uses page default)
 * @returns {string} The resolved product handle
 */
function getProductHandleByDiscountCode(discountCode, defaultHandle) {
  const fallbackHandle = defaultHandle || getPageDefaultProduct();
  if (!discountCode) return fallbackHandle;

  const code = discountCode.toUpperCase();

  const isTasterBoxContinuity = code.startsWith('OFF-TASTER');
  const isTasterBox4 = code.startsWith('SIP-3-');
  const isTasterBox3 = code.startsWith('GIN-TASTER-3-');
  const isTasterBox2 = code.startsWith('SIP-2-') || ['SIP-TASTER-FB2', 'SIP-TASTER-SOV2'].includes(code);
  const isTasterBox = !isTasterBox2 && (code.startsWith('SIP-') || code.startsWith('MM-SIP-'));

  const isRumTasterBox2 = code.startsWith('RUM-TASTER-2-');
  const isRumTasterBox = !isRumTasterBox2 && code.startsWith('RUM-TASTER-');

  const isGotmDiscount = code === '30OFF-HP';

  if (isTasterBoxContinuity) return 'gotm_taster_continuity';
  if (isRumTasterBox2) return 'rotm_taster_2';
  if (isRumTasterBox) return 'rotm_taster';
  if (isTasterBox4) return 'gotm_taster_4';
  if (isTasterBox3) return 'gotm_taster_3';
  if (isTasterBox2) return 'gotm_taster_2';
  if (isTasterBox) return 'gotm_taster';
  if (isGotmDiscount) return 'gotm';

  // Handle taster variations that should map to gotm
  if (!(isTasterBox || isTasterBox2 || isTasterBox3) && fallbackHandle.startsWith('gotm_taster')) {
    return 'gotm';
  }

  return fallbackHandle;
}

/**
 * Get product variant and selling plan from acquisition offer data
 *
 * @param {string} discountCode - The discount code to lookup
 * @param {string} [explicitProductHandle] - Optional explicit product handle (overrides discount-based lookup)
 * @returns {{ variantId: number, sellingPlanId: number, discountCode: string } | null}
 */
function getProductFromMetaobject(discountCode, explicitProductHandle) {
  const offers = window.__CGC?.acquisitionOffers;

  if (!Array.isArray(offers) || offers.length === 0) {
    console.error(
      '[LandingPageAddToCart] No acquisitionOffers data available. Ensure acquisition-offers.liquid is loaded.'
    );
    return null;
  }

  const normalizedDiscount = discountCode ? discountCode.toUpperCase() : null;
  const productHandle = explicitProductHandle || getProductHandleByDiscountCode(discountCode);

  const findByHandle = (candidateOffers) =>
    candidateOffers.find(
      (offer) =>
        Array.isArray(offer.handle_associations) && offer.handle_associations.some((handle) => handle === productHandle)
    );

  const discountOffers = normalizedDiscount
    ? offers.filter(
        (offer) =>
          Array.isArray(offer.applicable_discounts) &&
          offer.applicable_discounts.some((code) => String(code).toUpperCase() === normalizedDiscount)
      )
    : offers;

  let offer = findByHandle(discountOffers);

  if (!offer && discountOffers.length > 0) {
    offer = discountOffers[0];
  }

  if (!offer) {
    offer = findByHandle(offers);
  }

  if (!offer) {
    console.error(`[LandingPageAddToCart] No offer found for handle: ${productHandle} (discount: ${discountCode})`);
    return null;
  }

  if (!offer.variant_id || !offer.selling_plan_id) {
    console.error(
      `[LandingPageAddToCart] Offer missing variant_id or selling_plan_id for handle: ${productHandle} (discount: ${discountCode})`
    );
    return null;
  }

  return {
    variantId: offer.variant_id,
    sellingPlanId: offer.selling_plan_id,
    discountCode: discountCode || offer.applicable_discounts?.[0] || null,
  };
}

/**
 * Add item to cart via Shopify AJAX API
 *
 * @param {number} variantId - Product variant ID
 * @param {number} sellingPlanId - Selling plan ID for subscription
 * @param {object | null} properties - Custom properties to add to cart
 * @returns {Promise<Response>}
 */
async function addItemToCart(variantId, sellingPlanId, properties) {
  const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          selling_plan: sellingPlanId,
          quantity: 1,
          ...(properties ? {properties} : {}),
        },
      ],
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to add to cart: ${response.status}`);
  }

  return response.json();
}

/**
 * Apply discount code to cart via Shopify Storefront GraphQL API
 *
 * @param {string} discountCode - Discount code to apply
 * @returns {Promise<void>}
 */
async function applyDiscountToCart(discountCode) {
  if (!discountCode) return;

  // First get the cart ID
  const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
  const cartData = await cartResponse.json();

  // Convert numeric cart token to GraphQL cart ID format
  const cartId = `gid://shopify/Cart/${cartData.token}`;

  const query = `
    mutation CartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        cart {
          id
          discountCodes {
            code
            applicable
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const variables = {
    cartId,
    discountCodes: [discountCode],
  };

  const response = await fetch('https://craftgin.myshopify.com/api/2025-04/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  const result = await response.json();

  if (result.errors) {
    console.error('[LandingPageAddToCart] GraphQL discount error:', result.errors);
  }

  return result;
}

/**
 * Refresh cart sections for drawer update
 *
 * @returns {Promise<object>}
 */
async function refreshCartSections() {
  const sectionsUrl = `${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`;
  const response = await fetch(sectionsUrl);
  return response.json();
}

/**
 * Attach GA and Meta analytics data to cart attributes
 * Uses getAnalyticsData() from global.js
 *
 * @returns {Promise<void>}
 */
async function attachAnalyticsData() {
  try {
    // getAnalyticsData is defined in global.js
    if (typeof getAnalyticsData !== 'function') {
      console.warn('[LandingPageAddToCart] getAnalyticsData not available');
      return;
    }

    const { gaClientId, gaSessionId, fbp, fbc } = await getAnalyticsData();

    await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          ga_client_id: gaClientId,
          ga_session_id: gaSessionId,
          fbp: fbp,
          fbc: fbc,
        },
      }),
    });

    console.log('[LandingPageAddToCart] Analytics data attached to cart');
  } catch (error) {
    console.error('[LandingPageAddToCart] Error attaching analytics data:', error);
  }
}

/**
 * Open the cart drawer
 */
function openCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.open();
  } else {
    // Fallback: redirect to cart page
    window.location.href = window.routes?.cart_url || '/cart';
  }
}

/**
 * Main function to add a product to cart with discount and open drawer
 * Exported for use by announcement bar modal and other components
 *
 * @param {string} discountCode - The discount code (used for product lookup and checkout)
 * @param {string} [productHandle] - Optional explicit product handle (overrides discount-based lookup)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
async function addToCartWithDiscount(discountCode, productHandle) {
  try {
    // 1. Get product info from acquisition offers
    const product = getProductFromMetaobject(discountCode, productHandle);

    if (!product) {
      return {
        success: false,
        error: 'Could not find a product for this discount code. Please try a different code.',
      };
    }

    // 2. Apply discount code to cart
    if (product.discountCode) {
      await applyDiscountToCart(product.discountCode);
    }

    // 3. Add to cart
    const properties = this.getCustomProperties(product);
    await addItemToCart(product.variantId, product.sellingPlanId, properties);

    // 4. Refresh cart sections and get updated data
    const sections = await refreshCartSections();

    // 5. Publish cart update event
    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'landing-page-add-to-cart',
        cartData: sections,
      });
    }

    // 6. Update cart drawer content and open
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && sections['cart-drawer']) {
      // Update drawer HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const drawerContent = doc.querySelector('#CartDrawer');
      if (drawerContent) {
        const currentDrawer = document.querySelector('#CartDrawer');
        if (currentDrawer) {
          currentDrawer.innerHTML = drawerContent.innerHTML;
        }
      }

      // Update cart icon bubble
      if (sections['cart-icon-bubble']) {
        const iconBubble = document.querySelector('#cart-icon-bubble');
        const bubbleDoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = bubbleDoc.querySelector('.shopify-section');
        if (iconBubble && newBubble) {
          iconBubble.innerHTML = newBubble.innerHTML;
        }
      }

      // Remove empty class if present
      cartDrawer.classList.remove('is-empty');
      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.classList.remove('is-empty');
      }
    }

    // 7. Open cart drawer
    openCartDrawer();

    // 8. Attach GA and Meta analytics data to cart (fire-and-forget, non-blocking)
    attachAnalyticsData();

    return { success: true };
  } catch (error) {
    console.error('[LandingPageAddToCart] Error:', error);
    return {
      success: false,
      error: 'Something went wrong adding to cart. Please try again.',
    };
  }
}

/**
 * Get acquisition offer for applicable discount code
 * @param {object} product - object containing variantId, sellingPlanId and discount code
 * @returns {metaobject | null}
 */
function getApplicableOffer(product) {
  const offers = window.__CGC.acquisitionOffers;

  const applicableOffers = offers.filter(offer =>
    offer.applicable_discounts.some(code => code == product.discountCode)
  );

  if (!applicableOffers.length) {
    return;
  }

  const exactOffer = applicableOffers.find(offer => 
    offer.variant_id == product.variantId
  );

  const applicableOffer = exactOffer || applicableOffers[0];

  return applicableOffer;
}

/**
 * Get acquisition offer and make custom properties
 * @param {object} product - object containing variantId, sellingPlanId and discount code
 * 
 * @returns { object | null}
 */
function getCustomProperties(product) {
  const offer = this.getApplicableOffer(product);
  if (!offer || !offer.next_product_variant && !offer.next_discounts) {
    return null;
  }

  const properties = {
    ...(offer.next_product_variant
        ? { _next_product_variant_id: offer.next_product_variant.id } 
        : {}
      ),
    ...(offer.next_discounts
        ? { _next_discounts: offer.next_discounts.join(',') } 
        : {}
      )
  };

  return properties;
}

/**
 * Get discount code from URL parameter or page default
 *
 * @returns {string | null}
 */
function getDiscountCodeFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('discount_code') || params.get('discount') || window.__CGC?.pageDefaultDiscountCode || null;
}

/**
 * Get default product handle for the current page
 *
 * @returns {string}
 */
function getPageDefaultProduct() {
  return window.__CGC?.pageDefaultProduct || 'gotm';
}

/**
 * Parse hash parameters for add to cart
 * Supports formats:
 *   - #add_to_cart                     -> { discount: null, product: null }
 *   - #add_to_cart:DISCOUNT            -> { discount: 'DISCOUNT', product: null }
 *   - #add_to_cart::PRODUCT            -> { discount: null, product: 'PRODUCT' }
 *   - #add_to_cart:DISCOUNT:PRODUCT    -> { discount: 'DISCOUNT', product: 'PRODUCT' }
 *
 * @param {string} hash - The URL hash
 * @returns {{ discount: string | null, product: string | null }}
 */
function parseHashParams(hash) {
  if (!hash.startsWith('#add_to_cart')) {
    return { discount: null, product: null };
  }

  const parts = hash.split(':');

  if (parts.length === 1) {
    // #add_to_cart
    return { discount: null, product: null };
  }

  if (parts.length === 2) {
    // #add_to_cart:DISCOUNT or #add_to_cart: (empty)
    const discount = parts[1] || null;
    return { discount, product: null };
  }

  // #add_to_cart:DISCOUNT:PRODUCT or #add_to_cart::PRODUCT
  const discount = parts[1] || null;
  const product = parts.slice(2).join(':') || null; // Handle product handles that might contain colons
  return { discount, product };
}

/**
 * Handle click on add-to-cart links
 *
 * @param {Event} event - Click event
 */
async function handleAddToCartClick(event) {
  const link = event.target.closest('a[href^="#add_to_cart"]');
  if (!link) return;

  event.preventDefault();
  event.stopPropagation();

  const hash = link.getAttribute('href');

  // Parse hash for discount code and product handle
  const hashParams = parseHashParams(hash);

  // Get discount code from hash or URL param (or page default)
  const discountCode = hashParams.discount || getDiscountCodeFromUrl();

  // Product handle is optional - if not specified, it's derived from discount code
  const productHandle = hashParams.product;

  // Show loading state on button
  const originalText = link.textContent;
  link.textContent = 'Adding...';
  link.style.pointerEvents = 'none';

  const result = await addToCartWithDiscount(discountCode, productHandle);

  // Reset button state
  link.textContent = originalText;
  link.style.pointerEvents = '';

  if (!result.success) {
    alert(result.error);
  }
}

/**
 * Initialize event listeners
 */
function init() {
  // Use event delegation for handling add-to-cart links
  document.addEventListener('click', handleAddToCartClick);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for use by other modules (e.g., discount-validation.js)
window.__CGC = window.__CGC || {};
window.__CGC.addToCartWithDiscount = addToCartWithDiscount;
